import type Quill from 'quill/core';
import { useEffect } from 'react';
import type { BindingObject } from '../../BaseKeyboard';
import { useQuill } from '../../QuillContext';
import type TableEmbed from '../../formats/TableEmbed';
import type { BlockEmbedMovedInOptions } from '../../types';
import {
  getSiblingLine,
  isRangeACaret,
  isRangeAtLineVerticalEdge,
} from '../../utils/keyboardUtils';
import moveCaretIntoLineWithKeyboard from '../../utils/moveCaretIntoLineWithKeyboard';
import { CELL_IDENTITY_ATTRIBUTE } from '../constants';
import { parseCellIdentity, stringifyCellIdentity } from '../utils';

const handleMoveOut = (
  quill: Quill,
  ancestorQuill: Quill,
  movedInOptions: BlockEmbedMovedInOptions,
) => {
  const cell = quill.root.closest(`[${CELL_IDENTITY_ATTRIBUTE}]`);
  const parentNode = cell?.closest('table');
  if (!cell || !parentNode) return;
  const blot = ancestorQuill.scroll.find(parentNode, true) as TableEmbed | null;
  if (!blot) return;
  const rowCount = parentNode.querySelectorAll('tr').length;
  const columnCount = parentNode.querySelectorAll('tr:first-child td').length;
  const identity = cell.getAttribute(CELL_IDENTITY_ATTRIBUTE);
  if (!identity) return;
  const [row, col] = parseCellIdentity(identity);
  switch (movedInOptions.direction) {
    case 'left':
      if (col > 0) {
        blot.moveCaretIntoCellWithKeyboard(
          stringifyCellIdentity(row, col - 1),
          movedInOptions,
        );
        return;
      }
      if (row > 0) {
        blot.moveCaretIntoCellWithKeyboard(
          stringifyCellIdentity(row - 1, columnCount - 1),
          movedInOptions,
        );
        return;
      }
      break;
    case 'right':
      if (col < columnCount - 1) {
        blot.moveCaretIntoCellWithKeyboard(
          stringifyCellIdentity(row, col + 1),
          movedInOptions,
        );
        return;
      }
      if (row < rowCount - 1) {
        blot.moveCaretIntoCellWithKeyboard(
          stringifyCellIdentity(row + 1, 0),
          movedInOptions,
        );
        return;
      }
      break;
    case 'up':
      if (row > 0) {
        blot.moveCaretIntoCellWithKeyboard(
          stringifyCellIdentity(row - 1, col),
          movedInOptions,
        );
        return;
      }
      break;
    case 'down':
      if (row < rowCount - 1) {
        blot.moveCaretIntoCellWithKeyboard(
          stringifyCellIdentity(row + 1, col),
          movedInOptions,
        );
        return;
      }
      break;
    default:
      break;
  }

  // Moving out of the table
  const sibling = getSiblingLine(
    blot,
    movedInOptions.direction === 'left' || movedInOptions.direction === 'up'
      ? 'previous'
      : 'next',
  );
  if (sibling) {
    moveCaretIntoLineWithKeyboard(sibling, movedInOptions);
  }
};

const useCellKeyboardShortcuts = () => {
  const { quill, ancestorQuill } = useQuill();
  useEffect(() => {
    if (!quill || !ancestorQuill) return undefined;
    const tabBinding: BindingObject = {
      key: 'Tab',
      shiftKey: null,
      shortKey: false,
      format: { list: false },
      handler: (_range, { event }) => {
        handleMoveOut(quill, ancestorQuill, {
          direction: event.shiftKey ? 'left' : 'right',
        });
        return false;
      },
    };
    const arrowLeftRightBinding: BindingObject = {
      key: ['ArrowLeft', 'ArrowRight'],
      handler: (range, { event, line, format }) => {
        if (!isRangeACaret(range, line)) return true;

        const isLeftKey = event.key === 'ArrowLeft';
        const { index } = range;
        if (
          (isLeftKey && index > 0) ||
          (!isLeftKey && index < quill.getLength() - 1)
        ) {
          return true;
        }

        if (format.code && !isLeftKey) {
          quill.format('code', false);
        } else {
          handleMoveOut(quill, ancestorQuill, {
            direction: isLeftKey ? 'left' : 'right',
          });
        }
        return false;
      },
    };
    const arrowUpDownBinding: BindingObject = {
      key: ['ArrowUp', 'ArrowDown'],
      handler: (range, { event, line }) => {
        const isUpKey = event.key === 'ArrowUp';

        if (getSiblingLine(line, isUpKey ? 'previous' : 'next')) {
          return true;
        }
        if (
          !isRangeAtLineVerticalEdge(
            range,
            line,
            event.key === 'ArrowUp' ? 'top' : 'bottom',
          )
        ) {
          return true;
        }
        const selectionBounds = quill.selection.getBounds(range.index);
        if (!selectionBounds) return true;

        handleMoveOut(quill, ancestorQuill, {
          direction: isUpKey ? 'up' : 'down',
          xOffset: selectionBounds?.left,
        });
        return false;
      },
    };
    const selectAllBinding: BindingObject = {
      key: 'a',
      shortKey: true,
      handler: () => {
        quill.setSelection(0, quill.getLength());
      },
    };

    // @ts-expect-error
    quill.keyboard.prependBinding(arrowLeftRightBinding);
    quill.keyboard.addBinding(arrowUpDownBinding);
    // @ts-expect-error
    quill.keyboard.prependBinding(tabBinding);
    quill.keyboard.addBinding(selectAllBinding);
    return () => {
      // @ts-expect-error
      quill.keyboard.removeBinding(arrowLeftRightBinding);
      // @ts-expect-error
      quill.keyboard.removeBinding(arrowUpDownBinding);
      // @ts-expect-error
      quill.keyboard.removeBinding(tabBinding);
      // @ts-expect-error
      quill.keyboard.removeBinding(selectAllBinding);
    };
  }, [quill, ancestorQuill]);
};

export default useCellKeyboardShortcuts;
